/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import moment from "moment";
import 'moment-timezone';

export const getCurrentTimestamp = () => {
  return Math.floor(Date.now() / 1000);
};

export const unixEpochToString = (timestamp, showTime = true, timezone = null,  format = null) => {
  // handle null
  if (timestamp == null) {
    return null;
  }

  // convert Unix timestamp to milliseconds
  const date = new Date(timestamp * 1000);

  // create moment object and apply timezone
  let momentObj = moment(date);
  if (timezone) {
    momentObj = momentObj.tz(timezone);
  }

  // convert to human-readable date string
  if (format) {
    return momentObj.format(format);
  }
  
  const dateString = momentObj.format(showTime ? "M/D/yy h:mm A" : "M/D/yy");

  return dateString;
};

export const secondsToTimeString = (seconds, suffix = "") => {
  let value = Math.round(seconds);
  if (value == null) {
    return null;
  }

  let string = ""

  if (value < 60) {
    return "right now";
  } else {
    const multipliers = [
      { name: "minute", mult: 60 },
      { name: "hour", mult: 24 },
      { name: "day", mult: 7 },
      { name: "week", mult: 52.143 },
      { name: "year", mult: null }
    ];

    value /= 60;
    multipliers.every((item) => {
      if (item.mult == null || value / item.mult < 1) {
        const intValue = Math.floor(value);
        string = `${intValue} ${item.name}${intValue === 1 ? "" : "s"}`
        return false;
      }
      value /= item.mult;
      return true;
    });
  }

  return string + suffix;
};

export const secondsToTimeStringShort = (time) => {
  if (time == null) {
    return null;
  }

  const secondsInMinute = 60;
  const secondsInHour = secondsInMinute * 60;
  const secondsInDay = secondsInHour * 24;

  const days = Math.floor(time / secondsInDay);
  const hours = Math.floor((time % secondsInDay) / secondsInHour);
  const minutes = Math.floor((time % secondsInHour) / secondsInMinute);
  const seconds = Math.floor(time % secondsInMinute);

  return `${days > 0 ? `${days}d ` : ''}${hours}h ${minutes}m ${seconds}s`;
}

export const secondsToTimeStringSession = (time) => {
  if (time == null) {
    return null;
  }

  const secondsInMinute = 60;
  const secondsInHour = secondsInMinute * 60;
  const secondsInDay = secondsInHour * 24;

  const days = Math.floor(time / secondsInDay);
  const hours = Math.floor((time % secondsInDay) / secondsInHour);
  const minutes = Math.ceil((time % secondsInHour) / secondsInMinute);

  return `${days > 0 ? `${days}d ` : ''}${hours}h ${minutes}m`;
}

export const secondsToDurationString = (seconds, suffix = "") => {
  let value = Math.round(seconds);
  if (value == null) {
    return null;
  }

  let string = ""

  if (value < 60) {
    string = `${seconds} second${seconds === 1 ? "" : "s"}`;
  } else {
    const multipliers = [
      { name: "minute", mult: 60 },
      { name: "hour", mult: 24 },
      { name: "day", mult: 7 },
      { name: "week", mult: 52.143 },
      { name: "year", mult: null }
    ];

    value /= 60;
    multipliers.every((item) => {
      if (item.mult == null || value / item.mult < 1) {
        const intValue = Math.floor(value);
        string = `${intValue} ${item.name}${intValue === 1 ? "" : "s"}`
        return false;
      }
      value /= item.mult;
      return true;
    });
  }

  return string + suffix;
};

export const getAgeFromBirthday = (birthday) => {
  try {
    // parse age string 
    const split = birthday.split("/");
    if (split.length !== 3) {
      return null;
    }

    // figure out what age string this is
    let day, month, year;
    if (split[0] > 100) {
      // yyyy/mm/dd
      day = split[2];
      month = split[1];
      year = split[0];
    }
    else if (split[2] > 100) {
      // mm/dd/yyyy
      day = split[1];
      month = split[0];
      year = split[2];
    }

    // get current date
    const today = new Date();
    
    // create date object based on  input day
    const birthDate = new Date(year, month - 1, day);

    // calculate and return age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch (e) {
    return null;
  }
};

export const getValueSuffix = (value) => {
  let suffix = "th";

  // check 10th - 20th (always ends in 'th')
  const valueMod100 = value % 100;
  const value11To13 = valueMod100 >= 11 && valueMod100 <= 13;

  // check if suffix check needed
  if (!value11To13) {
    switch (value % 10) {
      case 1: suffix = "st"; break;
      case 2: suffix = "nd"; break;
      case 3: suffix = "rd"; break;
      default: break;
    }
  }

  // return value with new suffix
  return `${value}${suffix}`;
};

export const formatDate = (day, month, year) => {
  if (day == null || month == null || year == null) {
    return null;
  }

  // add leading 0s to each value
  const formattedDay = String(day).padStart(2, '0');
  const formattedMonth = String(month).padStart(2, '0');
  const formattedYear = String(year).padStart(4, '0');

  // build and return date in yyyy/mm/dd format
  const formattedDate = `${formattedYear}/${formattedMonth}/${formattedDay}`;
  return formattedDate;
};